import { useState } from 'react';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import SEOHead from '../../components/base/SEOHead';

export default function ContactPage() {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

  const jsonLd = [
    {
      '@context': 'https://schema.org',
      '@type': 'ContactPage',
      '@id': `${siteUrl}/contact`,
      name: 'Contact YESJETS',
      url: `${siteUrl}/contact`,
      description: 'Contact YESJETS for private jet charter inquiries. Reach our 24/7 concierge team by phone, email, or the contact form.',
      breadcrumb: {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: siteUrl },
          { '@type': 'ListItem', position: 2, name: 'Contact', item: `${siteUrl}/contact` },
        ],
      },
    },
    {
      '@context': 'https://schema.org',
      '@type': 'LocalBusiness',
      '@id': `${siteUrl}/#organization`,
      name: 'YESJETS',
      url: siteUrl,
      telephone: '+17085678866',
      email: 'Flights@flyyesjets.com',
      priceRange: '$$$',
      address: {
        '@type': 'PostalAddress',
        addressLocality: 'Chicago',
        addressRegion: 'IL',
        addressCountry: 'US',
      },
      geo: {
        '@type': 'GeoCoordinates',
        latitude: 41.8781,
        longitude: -87.6298,
      },
      openingHoursSpecification: {
        '@type': 'OpeningHoursSpecification',
        dayOfWeek: ['Monday','Tuesday','Wednesday','Thursday','Friday','Saturday','Sunday'],
        opens: '00:00',
        closes: '23:59',
      },
    },
  ];

  const [formData, setFormData] = useState({ name: '', email: '', phone: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');
    try {
      const formBody = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => formBody.append(key, value));
      const response = await fetch('https://readdy.ai/api/form/d6o924t39lnhn4hh16ng', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: formBody.toString(),
      });
      if (response.ok) { setSubmitStatus('success'); setFormData({ name: '', email: '', phone: '', message: '' }); }
      else { setSubmitStatus('error'); }
    } catch { setSubmitStatus('error'); }
    finally { setIsSubmitting(false); }
  };

  return (
    <div className="min-h-screen bg-white">
      <SEOHead
        title="Contact YESJETS | 24/7 Private Jet Charter Concierge – Los Angeles"
        description="Contact YESJETS anytime. Call +1(708) 567-8866 or email Flights@flyyesjets.com for private jet charter inquiries. 24/7 concierge service, Los Angeles, CA."
        keywords="contact YESJETS, private jet inquiry, charter concierge, YESJETS phone number, Los Angeles aviation"
        canonical="/contact"
        jsonLd={jsonLd}
      />

      <Navbar />

      {/* Hero Section */}
      <section className="relative h-[40vh] md:h-[50vh] w-full overflow-hidden mt-16 md:mt-20">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/24a1f6d2a79b5c891a5a0668b0da913c.jpg"
            alt="Contact Us"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-black/50"></div>
        </div>

        <div className="relative h-full flex items-center justify-center px-6 md:px-8">
          <div className="text-center text-white">
            <h1 className="text-4xl sm:text-5xl md:text-7xl font-serif font-semibold mb-3 md:mb-4">
              GET IN TOUCH
            </h1>
            <p className="text-base md:text-xl font-sans text-gray-200">
              We're here to assist you 24/7
            </p>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-16 md:py-24 bg-[#faf9f7]">
        <div className="max-w-6xl mx-auto px-6 md:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 md:gap-20">
            {/* Contact Form */}
            <div>
              <h2 className="text-3xl md:text-4xl font-serif font-semibold text-black mb-6 md:mb-8">
                Send Us a Message
              </h2>

              <form onSubmit={handleSubmit} data-readdy-form id="contact-form" className="space-y-6">
                <div>
                  <input type="text" name="name" value={formData.name} onChange={handleChange} required placeholder="Your Name" className="w-full border-b-2 border-gold bg-transparent px-0 py-3 text-base text-black placeholder-gray-400 outline-none focus:border-gold/70 transition-colors" />
                </div>
                <div>
                  <input type="email" name="email" value={formData.email} onChange={handleChange} required placeholder="Email Address" className="w-full border-b-2 border-gold bg-transparent px-0 py-3 text-base text-black placeholder-gray-400 outline-none focus:border-gold/70 transition-colors" />
                </div>
                <div>
                  <input type="tel" name="phone" value={formData.phone} onChange={handleChange} required placeholder="Phone Number" className="w-full border-b-2 border-gold bg-transparent px-0 py-3 text-base text-black placeholder-gray-400 outline-none focus:border-gold/70 transition-colors" />
                </div>
                <div>
                  <textarea name="message" value={formData.message} onChange={handleChange} required maxLength={500} rows={5} placeholder="Your Message" className="w-full border-b-2 border-gold bg-transparent px-0 py-3 text-base text-black placeholder-gray-400 outline-none focus:border-gold/70 transition-colors resize-none" />
                  <div className="text-right text-sm text-gray-500 mt-1">{formData.message.length}/500</div>
                </div>

                <button type="submit" disabled={isSubmitting} className="w-full bg-black text-white py-4 rounded-full text-base font-sans font-semibold tracking-wide hover:bg-gold hover:text-black transition-all disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap">
                  {isSubmitting ? 'SENDING...' : 'SEND MESSAGE'}
                </button>

                {submitStatus === 'success' && (
                  <div className="bg-green-500/20 border border-green-500 text-green-700 px-6 py-4 rounded-lg text-center">Thank you! We'll get back to you shortly.</div>
                )}
                {submitStatus === 'error' && (
                  <div className="bg-red-500/20 border border-red-500 text-red-700 px-6 py-4 rounded-lg text-center">Something went wrong. Please try again.</div>
                )}
              </form>
            </div>

            {/* Contact Information */}
            <div>
              <h2 className="text-3xl md:text-4xl font-serif font-semibold text-black mb-6 md:mb-8">
                Contact Information
              </h2>

              <div className="space-y-7 md:space-y-8">
                {[
                  { icon: 'ri-phone-line', title: 'Phone Number', content: '+1(708) 567-8866' },
                  { icon: 'ri-mail-line', title: 'Email Address', content: 'Flights@flyyesjets.com' },
                  { icon: 'ri-time-line', title: 'Operating Hours', content: '24/7 Concierge Service\nAvailable Every Day' },
                ].map((item, index) => (
                  <div key={index} className="flex gap-5 md:gap-6">
                    <div className="w-12 h-12 rounded-full bg-gold/10 border-2 border-gold flex items-center justify-center flex-shrink-0">
                      <i className={`${item.icon} text-xl text-gold`}></i>
                    </div>
                    <div>
                      <h3 className="text-base md:text-lg font-sans font-semibold text-black mb-2">{item.title}</h3>
                      <p className="text-gray-600 font-sans leading-relaxed text-sm md:text-base whitespace-pre-line">{item.content}</p>
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-10 md:mt-12">
                <h3 className="text-base md:text-lg font-sans font-semibold text-black mb-5 md:mb-6">Follow Us</h3>
                <div className="flex gap-4">
                  {['ri-instagram-line', 'ri-linkedin-fill', 'ri-facebook-fill', 'ri-twitter-x-line'].map((icon, i) => (
                    <a key={i} href="#" className="w-12 h-12 rounded-full border-2 border-gold text-gold hover:bg-gold hover:text-black transition-all flex items-center justify-center cursor-pointer">
                      <i className={`${icon} text-xl`}></i>
                    </a>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
